import check from '../images/check-white.svg';
import locksvgrepocom from '../images/lock.svg';
import diploma from '../images/diploma.svg';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { VersionItem, VersionAccountStatus, DetailedLesson } from '../types';
import { twMerge } from 'tailwind-merge';

type VersionProgressProps = {
    version: VersionItem;
    lessons: DetailedLesson[];
    activeLessonId: number;
    setDisplayedLessonId: (value: number) => void;
    accountVersionStatus: VersionAccountStatus;
    setShowSuccess: (value: boolean) => void;
    showSuccess: boolean;
    navigate: (path: string) => void;
    validatedLesson: boolean;
};

function VersionProgress({
    version,
    lessons,
    activeLessonId,
    setDisplayedLessonId,
    accountVersionStatus,
    setShowSuccess,
    showSuccess,
    navigate,
    validatedLesson,
}: VersionProgressProps) {
    const [scrollPosition, setScrollPosition] = useState(0);
    const { formationId, versionId } = useParams();
    const [lessonStatus, setLessonStatus] = useState<{ [lessonId: number]: boolean }>({});

    const handleScroll = () => {
        setScrollPosition(window.scrollY);
    };

    useEffect(() => {
        setLessonStatus(accountVersionStatus.lesson_statuses);
    }, [accountVersionStatus, lessonStatus]);

    const isLessonCompleted = (lessonId: number) => {
        return lessonStatus[lessonId] || false;
    };
    const isLessonAccessible = (currentIndex: number) => {
        // First lesson is always accessible
        if (currentIndex === 0) return true;

        // Check if all previous lessons are completed
        return lessons.slice(0, currentIndex).every(lesson => isLessonCompleted(lesson.id));
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [accountVersionStatus]);

    return (
        <div
            className={`width-full absolute left-0 right-0 top-[65px] m-auto bg-white/75 backdrop-blur-xl h-[65px] pointer-events-auto border border-[#EAECF0] px-3 sm:px-6 md:px-32 lg:bg-transparent lg:backdrop-blur-none lg:top-[10px] lg:w-fit lg:border-none transition-all duration-200 ${
                scrollPosition > 50 ? 'scale-100 -mt-1 lg:top-[3px]' : 'scale-100'
            }`}
        >
            <div className="relative m-auto flex h-full w-full flex-row items-center justify-between lg:w-fit lg:gap-10">
                <div className={`absolute bottom-0 top-0 z-0 m-auto h-[1px] w-full rounded-full bg-[#E5E5E5]`}></div>

                {lessons &&
                    lessons.map(function (lesson, index) {
                        const completed = isLessonCompleted(lesson.id);
                        const isActive = activeLessonId === lesson.id;
                        const isAccessible = isLessonAccessible(index);

                        return (
                            <div key={index.toString()}>
                                {isActive && !showSuccess ? (
                                    <div
                                        className={twMerge(
                                            `relative border z-10 flex h-[30px] w-[30px]  items-center cursor-pointer justify-center rounded-full md:h-[34px] md:w-[34px] ',
                                             bg-ar-${version?.formation?.theme}`
                                        )}
                                    >
                                        <span className={twMerge('absolute text-md font-bold text-white')}>
                                            {lesson.id}
                                        </span>
                                        {completed && (
                                            <div
                                                className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-ar-${version?.formation?.theme} shadow-indigoxs`}
                                            >
                                                <img src={check} alt="Check" />
                                            </div>
                                        )}
                                    </div>
                                ) : completed ? (
                                    <div
                                        onClick={() => {
                                            setShowSuccess(false);
                                            isAccessible && setDisplayedLessonId(lesson.id);
                                            navigate(
                                                `/formation/${formationId}/version/${versionId}/lesson/${lesson.id}`
                                            );
                                        }}
                                        key={index.toString()}
                                        className={`z-10 relative flex h-[30px] w-[30px] items-center ${
                                            isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'
                                        } justify-center rounded-full md:h-[34px] md:w-[34px] border border-[#E5E5E5] bg-white`}
                                    >
                                        <span className={`text-md font-bold  text-[#3D3D3D]`}>{index + 1}</span>
                                        <div
                                            className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-ar-${version?.formation?.theme} shadow-indigoxs`}
                                        >
                                            <img src={check} alt="Check" />
                                        </div>
                                    </div>
                                ) : lesson.id === activeLessonId + 1 && isAccessible ? (
                                    <div
                                        onClick={() => {
                                            setShowSuccess(false);
                                            setDisplayedLessonId(lesson.id);
                                            navigate(
                                                `/formation/${formationId}/version/${versionId}/lesson/${lesson.id}`
                                            );
                                        }}
                                        key={index.toString()}
                                        className={`z-10 relative flex h-[30px] w-[30px] items-center cursor-pointer justify-center rounded-full md:h-[34px] md:w-[34px] border border-[#E5E5E5] bg-white`}
                                    >
                                        <span className={`text-md font-bold text-[#3D3D3D]`}>{index + 1}</span>
                                    </div>
                                ) : !completed || !validatedLesson ? (
                                    <div
                                        className={`z-10 relative flex h-[30px] w-[30px] items-center justify-center rounded-full md:h-[34px] md:w-[34px] border border-[#E5E5E5] bg-white`}
                                    >
                                        <span className={`text-md font-bold text-[#3D3D3D] select-none`}>
                                            {index + 1}
                                        </span>
                                        <div
                                            className={`absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm`}
                                        >
                                            <img className="w-3 h-3" src={locksvgrepocom} alt="Check" />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}

                {accountVersionStatus.finished === true && (
                    <div
                        onClick={() => {
                            setShowSuccess(true);

                            navigate(`/formation/${formationId}/version/${versionId}/lesson/success`);
                        }}
                        className={`z-10 flex h-[30px] w-[30px] items-center cursor-pointer justify-center rounded-full md:h-[34px] md:w-[34px] bg-white shadow-md`}
                    >
                        <img src={diploma} alt="diploma" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default VersionProgress;
